import { trackDesignSystemAction } from 'analytics';
import forge from 'apis/forge';
import { UserContact } from 'dragonmouse-shared/interfaces';
import Icon, { IconType } from 'components/Icon';
import Modal from 'components/Modal';
import DropdownMenu from 'components/Widget/DropdownMenu';
import { Loader } from 'components/Widget/Loader';
import { MenuItemAction } from 'menu/MenuItem';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import {
	setForceTransientSidebarClosed,
	setForceTransientSidebarReleasePending,
	setSidebarHovered,
} from 'redux/mode/actions';
import { toast } from 'react-toastify';
import {
	selectDesignSystems,
	selectFrame,
	selectOrganizations,
	selectScenes,
	selectUser,
} from 'redux/db/reducers';
import { canEdit, canEditFrame, designSystemEditingBlocked } from 'redux/db/util';
import { selectIsMobile } from 'redux/mode/reducers';
import { RootReducer } from 'redux/reducers';
import { designSystemShareUrl, frameShareUrl, sceneShareUrl, validEmail } from 'utils';
import AccessList, { AccessOrg } from './AccessList';
import ComposeDrawer from './ComposeDrawer';
import EmailChipInput from './EmailChipInput';
import LinkAccess from './LinkAccess';
import * as S from './styles';

export type ShareEntityKind = 'scene' | 'designSystem' | 'frame';

interface ShareModalProps {
	kind: ShareEntityKind;
	entityId: string;
	onClose: () => void;
}

interface ShareTarget {
	title: string;
	entityLabel: string;
	shareUrl: string;
	shareTitle: string;
	publiclyReadable: boolean;
	canChangeAccess: boolean;
	canManageAccess: boolean;
	trackAction:
		| 'share_scene_link'
		| 'copy_scene_link'
		| 'share_design_system_link'
		| 'copy_design_system_link'
		| 'share_frame_link'
		| 'copy_frame_link';
	// publicly-readable target — frames have their own flag, scenes/DS use entity-level
	updatePublic: (next: boolean) => Promise<void>;
	// access management — scoped to scene for frames
	accessEntityId: string;
	getUsers: (id: string) => Promise<{ emails: string[]; pendingEmails: string[] }>;
	grantAccess: (id: string, emails: string[], notify: boolean, message: string) => Promise<void>;
	revokeAccess: (id: string, email: string) => Promise<void>;
	getOrganizations: (
		id: string
	) => Promise<{ organizations: { organizationId: string; organizationName: string }[] }>;
	addToOrganization: (entityId: string, orgId: string) => Promise<void>;
	removeFromOrganization: (entityId: string, orgId: string) => Promise<void>;
	onAccessGranted?: () => void;
}

const ShareModal = ({ kind, entityId, onClose }: ShareModalProps) => {
	//----------------------------------------------------------------------
	// HOOKS — REDUX
	//----------------------------------------------------------------------

	const dispatch = useDispatch();

	// A transient (hover-driven) sidebar should not linger while a modal it triggered is open.
	useEffect(() => {
		dispatch(setSidebarHovered(false));
		dispatch(setForceTransientSidebarClosed(true));
		return () => {
			// Don't drop the flag directly — the cursor may still be over the sidebar (the
			// modal opened over it), and a synthetic mouseEnter would fire as the modal
			// portal unmounts. Mark release pending; the sidebar drops the flag once the
			// cursor actually leaves (or right away if it's already not over it).
			dispatch(setForceTransientSidebarReleasePending(true));
		};
	}, [dispatch]);

	const { scene, designSystem, frame, frameScene, isMobile, authUser, user, userOrgs } =
		useSelector((r: RootReducer) => {
			const sceneEntity =
				kind === 'scene' ? selectScenes(r.db).find((s) => s.id === entityId) ?? null : null;
			const ds =
				kind === 'designSystem'
					? selectDesignSystems(r.db).find((d) => d.id === entityId) ?? null
					: null;
			const fr = kind === 'frame' ? selectFrame(r.db, entityId) : null;
			const frScene =
				fr && fr.sceneId ? selectScenes(r.db).find((s) => s.id === fr.sceneId) ?? null : null;
			return {
				scene: sceneEntity,
				designSystem: ds,
				frame: fr,
				frameScene: frScene,
				isMobile: selectIsMobile(r.mode),
				authUser: r.mode.authUser,
				user: selectUser(r.db),
				userOrgs: selectOrganizations(r.db),
			};
		}, shallowEqual);

	const canNativeShare =
		isMobile && typeof navigator !== 'undefined' && typeof navigator.share === 'function';

	//----------------------------------------------------------------------
	// HOOKS — ENTITY WIRING
	//----------------------------------------------------------------------

	const shareTarget = useMemo<ShareTarget | null>(() => {
		if (kind === 'scene' && scene) {
			const editable = canEdit(scene);
			return {
				title: 'Share project',
				entityLabel: 'project',
				shareUrl: sceneShareUrl(scene),
				shareTitle: scene.title ?? 'Moonchild project',
				publiclyReadable: !!scene.metadata?.publiclyReadable,
				canChangeAccess: editable,
				canManageAccess: editable,
				trackAction: canNativeShare ? 'share_scene_link' : 'copy_scene_link',
				updatePublic: async (next) => {
					await forge.updateScene(
						{
							update: {
								id: scene.id,
								metadata: { ...scene.metadata, publiclyReadable: next },
							},
						},
						true
					);
				},
				accessEntityId: scene.id,
				getUsers: async (id) => {
					const res = await forge.getSceneUsers({ sceneId: id });
					return {
						emails: res.userContacts.map((contact: UserContact) => contact.email),
						pendingEmails: res.pendingEmails,
					};
				},
				grantAccess: (id, emails, notify, message) =>
					forge.grantSceneAccess.withToast()({ sceneId: id, emails, notify, message }),
				revokeAccess: (id, email) => forge.revokeSceneAccess.withToast()({ sceneId: id, email }),
				getOrganizations: (id) => forge.getSceneOrganizations(id),
				addToOrganization: (eid, orgId) => forge.addSceneToOrganization.withToast()(eid, orgId),
				removeFromOrganization: (eid, orgId) =>
					forge.removeSceneFromOrganization.withToast()(eid, orgId),
				onAccessGranted: () => {
					if (user?.id) {
						forge.updateUser({
							update: {
								id: user.id,
								metadata: { lastSharedDate: new Date().toISOString() },
							},
						});
					}
				},
			};
		}

		if (kind === 'designSystem' && designSystem) {
			const editBlocked = designSystemEditingBlocked();
			return {
				title: 'Share design system',
				entityLabel: 'design system',
				shareUrl: designSystemShareUrl(designSystem),
				shareTitle: designSystem.title ?? 'Design system',
				publiclyReadable: !!designSystem.metadata?.publiclyReadable,
				canChangeAccess: !editBlocked,
				canManageAccess: !editBlocked,
				trackAction: canNativeShare ? 'share_design_system_link' : 'copy_design_system_link',
				updatePublic: async (next) => {
					await forge.updateDesignSystem({
						update: {
							id: designSystem.id,
							metadata: { ...designSystem.metadata, publiclyReadable: next },
						},
					});
					trackDesignSystemAction('change_access', { public: next });
				},
				accessEntityId: designSystem.id,
				getUsers: (id) => forge.getDesignSystemUsers({ designSystemId: id }),
				grantAccess: (id, emails, notify, message) =>
					forge.grantDesignSystemAccess.withToast()({
						designSystemId: id,
						emails,
						notify,
						message,
					}),
				revokeAccess: (id, email) =>
					forge.revokeDesignSystemAccess.withToast()({
						designSystemId: id,
						email,
					}),
				getOrganizations: (id) => forge.getDesignSystemOrganizations(id),
				addToOrganization: (eid, orgId) =>
					forge.addDesignSystemToOrganization.withToast()(eid, orgId),
				removeFromOrganization: (eid, orgId) =>
					forge.removeDesignSystemFromOrganization.withToast()(eid, orgId),
				onAccessGranted: () => {
					if (user?.id) {
						forge.updateUser({
							update: {
								id: user.id,
								metadata: { lastSharedDate: new Date().toISOString() },
							},
						});
					}
				},
			};
		}

		if (kind === 'frame' && frame && frameScene) {
			const editable = canEditFrame(frame);
			return {
				title: 'Share prototype',
				entityLabel: 'prototype',
				shareUrl: frameShareUrl(frame),
				shareTitle: frame.title ?? 'Prototype',
				publiclyReadable: !!frame.metadata?.publiclyReadable,
				canChangeAccess: editable,
				// frame's editor list IS the parent scene's
				canManageAccess: editable,
				trackAction: canNativeShare ? 'share_frame_link' : 'copy_frame_link',
				updatePublic: async (next) => {
					await forge.updateFrame(
						{
							update: {
								id: frame.id,
								metadata: { ...frame.metadata, publiclyReadable: next },
							},
						},
						true
					);
				},
				accessEntityId: frameScene.id,
				getUsers: async (id) => {
					const res = await forge.getSceneUsers({ sceneId: id });
					return {
						emails: res.userContacts.map((contact: UserContact) => contact.email),
						pendingEmails: res.pendingEmails,
					};
				},
				grantAccess: (id, emails, notify, message) =>
					forge.grantSceneAccess.withToast()({
						sceneId: id,
						emails,
						notify,
						message,
						frameId: frame.id,
					}),
				revokeAccess: (id, email) => forge.revokeSceneAccess.withToast()({ sceneId: id, email }),
				getOrganizations: (id) => forge.getSceneOrganizations(id),
				addToOrganization: (eid, orgId) => forge.addSceneToOrganization.withToast()(eid, orgId),
				removeFromOrganization: (eid, orgId) =>
					forge.removeSceneFromOrganization.withToast()(eid, orgId),
				onAccessGranted: () => {
					if (user?.id) {
						forge.updateUser({
							update: {
								id: user.id,
								metadata: { lastSharedDate: new Date().toISOString() },
							},
						});
					}
				},
			};
		}

		return null;
	}, [kind, scene, designSystem, frame, frameScene, canNativeShare, user?.id]);

	//----------------------------------------------------------------------
	// HOOKS — STATE
	//----------------------------------------------------------------------

	const [chips, setChips] = useState<string[]>([]);
	const [composeMessage, setComposeMessage] = useState('');
	const [composeNotify, setComposeNotify] = useState(true);
	const [isSending, setIsSending] = useState(false);

	const [emails, setEmails] = useState<string[]>([]);
	const [pendingEmails, setPendingEmails] = useState<string[]>([]);
	const [usersLoaded, setUsersLoaded] = useState(false);
	const [removingEmail, setRemovingEmail] = useState<string | null>(null);

	const [entityOrgs, setEntityOrgs] = useState<
		{ organizationId: string; organizationName: string }[]
	>([]);
	const [orgsLoaded, setOrgsLoaded] = useState(false);
	const [addingOrgId, setAddingOrgId] = useState<string | null>(null);
	const [removingOrgId, setRemovingOrgId] = useState<string | null>(null);

	const [orgMemberEmails, setOrgMemberEmails] = useState<string[]>([]);

	//----------------------------------------------------------------------
	// HOOKS — FETCH ON OPEN
	//----------------------------------------------------------------------

	useEffect(() => {
		if (!shareTarget) return;
		let cancelled = false;
		const load = async () => {
			try {
				const res = await shareTarget.getUsers(shareTarget.accessEntityId);
				if (!cancelled) {
					setEmails(res.emails);
					setPendingEmails(res.pendingEmails);
				}
			} catch {
				if (!cancelled) toast.error('Failed to load access list');
			} finally {
				if (!cancelled) setUsersLoaded(true);
			}
		};
		void load();
		return () => {
			cancelled = true;
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [shareTarget?.accessEntityId]);

	useEffect(() => {
		if (!shareTarget) return;
		let cancelled = false;
		const load = async () => {
			try {
				const res = await shareTarget.getOrganizations(shareTarget.accessEntityId);
				if (!cancelled) setEntityOrgs(res.organizations);
			} catch {
				if (!cancelled) toast.error('Failed to load teams');
			} finally {
				if (!cancelled) setOrgsLoaded(true);
			}
		};
		void load();
		return () => {
			cancelled = true;
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [shareTarget?.accessEntityId]);

	useEffect(() => {
		// fetch org member emails for autocomplete
		if (!userOrgs.length) return;
		let cancelled = false;
		const load = async () => {
			const all = new Set<string>();
			await Promise.all(
				userOrgs.map(async (org) => {
					try {
						const res = await forge.getOrganizationUsers(org.id);
						for (const u of res.users) {
							if (u.email) all.add(u.email);
						}
					} catch {
						// silent — autocomplete is a nice-to-have
					}
				})
			);
			if (!cancelled) setOrgMemberEmails(Array.from(all));
		};
		void load();
		return () => {
			cancelled = true;
		};
	}, [userOrgs]);

	//----------------------------------------------------------------------
	// UTILITY
	//----------------------------------------------------------------------

	// emails with access (registered + pending) for filtering autocomplete
	const accessEmails = useMemo(
		() => new Set([...emails, ...pendingEmails].map((e) => e.toLowerCase())),
		[emails, pendingEmails]
	);

	// org rows merged from userOrgs + entityOrgs
	const orgs = useMemo<AccessOrg[]>(() => {
		const grantedIds = new Set(entityOrgs.map((o) => o.organizationId));
		const result: AccessOrg[] = [];
		const seen = new Set<string>();
		for (const o of entityOrgs) {
			result.push({ id: o.organizationId, name: o.organizationName, granted: true });
			seen.add(o.organizationId);
		}
		for (const o of userOrgs) {
			if (seen.has(o.id)) continue;
			result.push({ id: o.id, name: o.name, granted: grantedIds.has(o.id) });
		}
		return result;
	}, [entityOrgs, userOrgs]);

	// suggestions filtered to emails not already on the access list and not the current user
	const autocompleteEmails = useMemo(() => {
		const myEmail = authUser?.email?.toLowerCase();
		return orgMemberEmails.filter(
			(e) => e.toLowerCase() !== myEmail && !accessEmails.has(e.toLowerCase())
		);
	}, [orgMemberEmails, authUser?.email, accessEmails]);

	// any invalid email blocks submission (inline pill marks it red)
	const hasInvalidChip = useMemo(() => chips.some((c) => !validEmail(c)), [chips]);

	// constraint: at most one granted team. ungranted teams surface in the picker.
	const grantedOrg = useMemo(() => orgs.find((o) => o.granted) ?? null, [orgs]);
	const availableOrgs = useMemo(
		() => orgs.filter((o) => !o.granted).sort((a, b) => a.name.localeCompare(b.name)),
		[orgs]
	);

	//----------------------------------------------------------------------
	// HANDLERS
	//----------------------------------------------------------------------

	const onAccessChange = useCallback(
		async (next: boolean) => {
			if (!shareTarget) return;
			try {
				await shareTarget.updatePublic(next);
			} catch (e) {
				toast.error('Failed to update access');
				console.error(e);
			}
		},
		[shareTarget]
	);

	const onSend = useCallback(async () => {
		if (!shareTarget || chips.length === 0) return;
		// Defensive: the disabled-submit gate normally prevents this, but guard at the
		// mutation boundary so we never call grantAccess with malformed input.
		const invalid = chips.filter((c) => !validEmail(c));
		if (invalid.length) {
			toast.error(
				invalid.length === 1
					? `${invalid[0]} is not a valid email`
					: `Not valid emails: ${invalid.join(', ')}`
			);
			return;
		}
		const dup = chips.filter((e) => accessEmails.has(e.toLowerCase()));
		if (dup.length) {
			toast.error(
				dup.length === 1
					? `${dup[0]} already has access`
					: `These already have access: ${dup.join(', ')}`
			);
			return;
		}
		setIsSending(true);
		try {
			await shareTarget.grantAccess(
				shareTarget.accessEntityId,
				chips,
				composeNotify,
				composeMessage
			);
			shareTarget.onAccessGranted?.();
			toast.success(`Added ${chips.length} ${chips.length === 1 ? 'person' : 'people'}`);
			// refetch so server's active vs. pending resolution is reflected
			try {
				const fresh = await shareTarget.getUsers(shareTarget.accessEntityId);
				setEmails(fresh.emails);
				setPendingEmails(fresh.pendingEmails);
			} catch {
				// non-fatal — polling will reconcile
			}
			// clearing chips returns the modal to its access view
			setChips([]);
			setComposeMessage('');
		} catch (e) {
			console.error('Failed to grant access', e);
		} finally {
			setIsSending(false);
		}
	}, [shareTarget, chips, composeNotify, composeMessage, accessEmails]);

	const onRemoveUser = useCallback(
		async (email: string) => {
			if (!shareTarget) return;
			setRemovingEmail(email);
			try {
				await shareTarget.revokeAccess(shareTarget.accessEntityId, email);
				setEmails((prev) => prev.filter((e) => e !== email));
				setPendingEmails((prev) => prev.filter((e) => e !== email));
				toast.success(`Removed ${email}`);
			} catch (e) {
				console.error('Failed to revoke access', e);
			} finally {
				setRemovingEmail(null);
			}
		},
		[shareTarget]
	);

	const onAddOrg = useCallback(
		async (orgId: string) => {
			if (!shareTarget) return;
			setAddingOrgId(orgId);
			try {
				await shareTarget.addToOrganization(shareTarget.accessEntityId, orgId);
				const org = userOrgs.find((o) => o.id === orgId);
				if (org) {
					setEntityOrgs((prev) => [
						...prev,
						{ organizationId: org.id, organizationName: org.name },
					]);
				}
			} catch (e) {
				console.error('Failed to add team', e);
			} finally {
				setAddingOrgId(null);
			}
		},
		[shareTarget, userOrgs]
	);

	const onRemoveOrg = useCallback(
		async (orgId: string) => {
			if (!shareTarget) return;
			setRemovingOrgId(orgId);
			try {
				await shareTarget.removeFromOrganization(shareTarget.accessEntityId, orgId);
				setEntityOrgs((prev) => prev.filter((o) => o.organizationId !== orgId));
			} catch (e) {
				console.error('Failed to remove team', e);
			} finally {
				setRemovingOrgId(null);
			}
		},
		[shareTarget]
	);

	// Back from compose = clear chips, which auto-returns to access view
	const onBack = useCallback(() => setChips([]), []);

	//----------------------------------------------------------------------
	// RENDERING
	//----------------------------------------------------------------------

	if (!shareTarget) {
		// entity not found — render an empty closing modal
		return (
			<Modal title="Share" onClose={onClose}>
				<S.LoadingContainer>
					<S.LoadingText>Unable to load sharing for this item.</S.LoadingText>
				</S.LoadingContainer>
			</Modal>
		);
	}

	const isLoading = !usersLoaded || !orgsLoaded;
	// view is derived: any chips → composing
	const isComposing = chips.length > 0;

	const renderTeamPicker = () => {
		if (!shareTarget.canManageAccess) return null;
		if (grantedOrg) return null;
		if (availableOrgs.length === 0) return null;
		const adding = addingOrgId !== null;

		if (availableOrgs.length === 1) {
			const org = availableOrgs[0];
			return (
				<S.TeamPickerButton onClick={() => onAddOrg(org.id)} disabled={adding}>
					<Icon type={IconType.ADD} width={14} height={14} />
					Share with {org.name}
					{addingOrgId === org.id && <Loader size={12} />}
				</S.TeamPickerButton>
			);
		}

		const menuItems = availableOrgs.map(
			(org) =>
				new MenuItemAction(
					`Share with ${org.name}`,
					'',
					() => onAddOrg(org.id),
					addingOrgId === org.id
				)
		);
		return (
			<DropdownMenu menuItems={menuItems}>
				<S.TeamPickerButton disabled={adding}>
					<Icon type={IconType.ADD} width={14} height={14} />
					Share with a team
					<Icon type={IconType.FILLED_ARROW_DOWN} width={12} height={12} />
				</S.TeamPickerButton>
			</DropdownMenu>
		);
	};

	return (
		<Modal
			title={isComposing ? 'Add people' : shareTarget.title}
			submitLabel={isComposing ? (composeNotify ? 'Add and notify' : 'Add') : undefined}
			onClose={onClose}
			onBack={isComposing ? onBack : undefined}
			onSubmit={isComposing ? onSend : undefined}
			loading={isComposing && isSending}
			disabled={isComposing && (isSending || hasInvalidChip)}
		>
			<S.Base>
				{!isComposing && shareTarget.canManageAccess && <S.SectionTitle>Editors</S.SectionTitle>}
				{shareTarget.canManageAccess && (
					<EmailChipInput
						chips={chips}
						onChipsChange={setChips}
						suggestions={autocompleteEmails}
						disabled={isSending}
					/>
				)}
				{!isComposing && renderTeamPicker()}
				{isComposing ? (
					<ComposeDrawer
						message={composeMessage}
						notify={composeNotify}
						onMessageChange={setComposeMessage}
						onNotifyChange={setComposeNotify}
						disabled={isSending}
					/>
				) : isLoading ? (
					<S.LoadingContainer>
						<Loader size={24} />
						<S.LoadingText>Loading...</S.LoadingText>
					</S.LoadingContainer>
				) : (
					<>
						{shareTarget.canManageAccess && (
							<AccessList
								currentUserEmail={authUser?.email}
								emails={emails}
								pendingEmails={pendingEmails}
								onRemoveUser={onRemoveUser}
								removingEmail={removingEmail}
								grantedOrg={grantedOrg}
								onRemoveOrg={onRemoveOrg}
								removingOrgId={removingOrgId}
							/>
						)}
						<LinkAccess
							publiclyReadable={shareTarget.publiclyReadable}
							canChangeAccess={shareTarget.canChangeAccess}
							onAccessChange={onAccessChange}
							shareUrl={shareTarget.shareUrl}
							shareTitle={shareTarget.shareTitle}
							canNativeShare={canNativeShare}
							entityLabel={shareTarget.entityLabel}
							trackAction={shareTarget.trackAction}
						/>
					</>
				)}
			</S.Base>
		</Modal>
	);
};

export default ShareModal;
